import axiosInstance from '@/app/api/axiosInstance';
import { PaginatedResponse } from '@/app/types/api';
import {
    SalesRegisterItem,
    PurchaseRegisterItem,
    StockSummaryItem,
    LowStockItem,
    ProfitLossResponse,
    OutstandingReceivableItem,
    OutstandingPayableItem,
    SalesSummary,
    PurchaseSummary,
    StockValueSummary,
    ReceivablesSummary,
    PayablesSummary,
    PartyStatementResponse,
    PartyClosingBalanceResponse,
    AccountStatementResponse,
    AccountClosingBalanceResponse
} from '@/app/types/reports';
import { 
    LowStockItemEnhanced, 
    LowStockReportParams, 
    LowStockReportResponse 
} from '@/app/types/lowStockReport';
import { format } from 'date-fns';

// Fetch sales register
export const fetchSalesRegister = async (params: {
    start_date: string;
    end_date: string;
    party_id?: number;
    skip?: number;
    limit?: number;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
}): Promise<PaginatedResponse<SalesRegisterItem>> => {
    const response = await axiosInstance.get<PaginatedResponse<SalesRegisterItem>>(
        '/reports/sales-register',
        { params }
    );
    return response.data;
};

// Get summary of sales for dashboard
export const fetchSalesSummary = async (params: {
    start_date: string;
    end_date: string;
}): Promise<SalesSummary> => {
    // Since we don't have a dedicated endpoint for summary, we'll use the register
    // with a small limit and calculate the summary from it
    const response = await fetchSalesRegister({
        ...params,
        limit: 1,  // We just need the total count
    });
    
    // Calculate summary from the paginated response
    return {
        total_sales: response.items.reduce((sum, item) => sum + item.total_amount, 0),
        sales_count: response.total,
        start_date: params.start_date,
        end_date: params.end_date
    };
};

// Fetch purchase register
export const fetchPurchaseRegister = async (params: {
    start_date: string;
    end_date: string;
    party_id?: number;
    skip?: number;
    limit?: number;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
}): Promise<PaginatedResponse<PurchaseRegisterItem>> => {
    const response = await axiosInstance.get<PaginatedResponse<PurchaseRegisterItem>>(
        '/reports/purchase-register',
        { params }
    );
    return response.data;
};

// Get summary of purchases for dashboard
export const fetchPurchaseSummary = async (params: {
    start_date: string;
    end_date: string;
}): Promise<PurchaseSummary> => {
    // Since we don't have a dedicated endpoint for summary, we'll use the register
    // with a small limit and calculate the summary from it
    const response = await fetchPurchaseRegister({
        ...params,
        limit: 1,  // We just need the total count
    });
    
    // Calculate summary from the paginated response
    return {
        total_purchases: response.items.reduce((sum, item) => sum + item.total_amount, 0),
        purchase_count: response.total,
        start_date: params.start_date,
        end_date: params.end_date
    };
};

// Fetch stock summary
export const fetchStockSummary = async (params?: {
    godown_id?: number;
    item_id?: number;
    skip?: number;
    limit?: number;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
}): Promise<PaginatedResponse<StockSummaryItem>> => {
    const response = await axiosInstance.get<PaginatedResponse<StockSummaryItem>>(
        '/reports/stock-summary',
        { params }
    );
    return response.data;
};

// Get summary of stock value for dashboard
export const fetchStockValueSummary = async (): Promise<StockValueSummary> => {
    // Get summary from stock summary
    const response = await fetchStockSummary({
        limit: 1000,  // Get all or a large number
    });
    
    // Calculate total value
    const totalValue = response.items.reduce((sum, item) => sum + item.closing_value, 0);
    
    return {
        total_stock_value: totalValue,
        item_count: response.total
    };
};

// Fetch low stock summary
export const fetchLowStockSummary = async (params?: {
    godown_id?: number;
    item_id?: number;
    skip?: number;
    limit?: number;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
}): Promise<PaginatedResponse<LowStockItem>> => {
    const response = await axiosInstance.get<PaginatedResponse<LowStockItem>>(
        '/reports/low-stock-summary',
        { params }
    );
    return response.data;
};

// Fetch enhanced low stock summary with cross-site visibility
export const fetchLowStockSummaryEnhanced = async (
    params?: LowStockReportParams
): Promise<LowStockReportResponse> => {
    const response = await axiosInstance.get<LowStockReportResponse>(
        '/reports/low-stock-summary-enhanced',
        { params }
    );
    return response.data;
};

// Fetch profit & loss report
export const fetchProfitLoss = async (params: {
    start_date: string;
    end_date: string;
}): Promise<ProfitLossResponse> => {
    const response = await axiosInstance.get<ProfitLossResponse>(
        '/reports/profit-loss',
        { params }
    );
    return response.data;
};

// Fetch outstanding receivables
export const fetchOutstandingReceivables = async (params?: {
    as_of_date?: string;
    party_id?: number;
    skip?: number;
    limit?: number;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
}): Promise<PaginatedResponse<OutstandingReceivableItem>> => {
    const response = await axiosInstance.get<PaginatedResponse<OutstandingReceivableItem>>(
        '/reports/outstanding-receivables',
        { params }
    );
    return response.data;
};

// Get summary of outstanding receivables for dashboard
export const fetchReceivablesSummary = async (asOfDate?: string): Promise<ReceivablesSummary> => {
    // Get date parameter
    const today = asOfDate || format(new Date(), 'yyyy-MM-dd');
    
    // Get receivables
    const response = await fetchOutstandingReceivables({
        as_of_date: today,
        limit: 1000  // Get all or a large number
    });
    
    // Calculate total and overdue
    const total = response.items.reduce((sum, item) => sum + item.amount_due, 0);
    const overdue = response.items
        .filter(item => (item.days_overdue || 0) > 0)
        .reduce((sum, item) => sum + item.amount_due, 0);
    
    // Count unique customers
    const uniqueCustomers = new Set(response.items.map(item => item.party_id));
    
    return {
        total_receivables: total,
        overdue_receivables: overdue,
        customer_count: uniqueCustomers.size
    };
};

// Fetch outstanding payables
export const fetchOutstandingPayables = async (params?: {
    as_of_date?: string;
    party_id?: number;
    skip?: number;
    limit?: number;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
}): Promise<PaginatedResponse<OutstandingPayableItem>> => {
    const response = await axiosInstance.get<PaginatedResponse<OutstandingPayableItem>>(
        '/reports/outstanding-payables',
        { params }
    );
    return response.data;
};

// Get summary of outstanding payables for dashboard
export const fetchPayablesSummary = async (asOfDate?: string): Promise<PayablesSummary> => {
    // Get date parameter
    const today = asOfDate || format(new Date(), 'yyyy-MM-dd');
    
    // Get payables
    const response = await fetchOutstandingPayables({
        as_of_date: today,
        limit: 1000  // Get all or a large number
    });
    
    // Calculate total and overdue
    const total = response.items.reduce((sum, item) => sum + item.amount_due, 0);
    const overdue = response.items
        .filter(item => (item.days_overdue || 0) > 0)
        .reduce((sum, item) => sum + item.amount_due, 0);
    
    // Count unique suppliers
    const uniqueSuppliers = new Set(response.items.map(item => item.party_id));
    
    return {
        total_payables: total,
        overdue_payables: overdue,
        supplier_count: uniqueSuppliers.size
    };
};

// Fetch party statement
export const fetchPartyStatement = async (params: {
    party_id: number;
    start_date: string;
    end_date: string;
}): Promise<PartyStatementResponse> => {
    const response = await axiosInstance.get<PartyStatementResponse>(
        `/reports/party-statement/${params.party_id}`,
        { 
            params: {
                start_date: params.start_date,
                end_date: params.end_date
            }
        }
    );
    return response.data;
};

// Fetch party closing balance
export const fetchPartyClosingBalance = async (params: {
    party_id: number;
    start_date: string;
    end_date: string;
}): Promise<PartyClosingBalanceResponse> => {
    const response = await axiosInstance.get<PartyClosingBalanceResponse>(
        `/reports/party-statement-closing-balance/${params.party_id}`,
        { 
            params: {
                start_date: params.start_date,
                end_date: params.end_date
            }
        }
    );
    return response.data;
};

// Fetch account statement
export const fetchAccountStatement = async (params: {
    account_id: number;
    start_date: string;
    end_date: string;
}): Promise<AccountStatementResponse> => {
    const response = await axiosInstance.get<AccountStatementResponse>(
        `/reports/account-statement/${params.account_id}`,
        { 
            params: {
                start_date: params.start_date,
                end_date: params.end_date
            }
        }
    );
    return response.data;
};

// Fetch account closing balance
export const fetchAccountClosingBalance = async (params: {
    account_id: number;
    start_date: string;
    end_date: string;
}): Promise<AccountClosingBalanceResponse> => {
    const response = await axiosInstance.get<AccountClosingBalanceResponse>(
        `/reports/account-statement-closing-balance/${params.account_id}`,
        { 
            params: {
                start_date: params.start_date,
                end_date: params.end_date
            }
        }
    );
    return response.data;
}; 